import type {
  AcknowledgeReviewPayload,
  CloseOneOnOneMeetingResponse,
  CreateActionItemPayload,
  CreateOneOnOnePayload,
  GetManagerReviewsResponse,
  ManagerReviewDetailResponse,
  MemberPerformanceResponse,
  MemberSharedReviewResponse,
  PerformanceSummaryResponse,
  EmployeeReviewSubmissionResponse,
  EmployeeReviewsOverviewResponse,
  EmployeeSharedReviewResponse,
  GetOneOnOnesResponse,
  OneOnOneActionItemResponse,
  OneOnOneDetailResponse,
  OneOnOneMeetingResponse,
  PerformancePeopleResponse,
  SaveReviewAnswersPayload,
  SetReviewPeersPayload,
  StartProbationReviewPayload,
  StartProbationReviewResponse,
  UpdateActionItemPayload,
  UpdateOneOnOneMeetingPayload,
  UpdateTalkingPointPayload,
} from '@gokrew/models/performance';
import type { PerformanceDeleteResponse } from '@/types/performance';
import api from './client';

/**
 * The employee's side of performance reviews and 1:1s. The company comes from
 * the token, so no route takes a `companyId`.
 */
const REVIEWS = '/employee/performance/reviews';
const MANAGER_REVIEWS = '/employee/performance/manager-reviews';
const ONE_ON_ONES = '/employee/performance/one-on-ones';
const member = (employeeId: string) =>
  `/employee/my-team/members/${employeeId}/performance`;

const endpoints = {
  summary: '/employee/performance/summary',
  managerReviews: MANAGER_REVIEWS,
  managerReview: (reviewId: string) => `${MANAGER_REVIEWS}/${reviewId}`,
  managerReviewAnswers: (reviewId: string) =>
    `${MANAGER_REVIEWS}/${reviewId}/answers`,
  managerReviewShare: (reviewId: string) =>
    `${MANAGER_REVIEWS}/${reviewId}/share`,
  memberPerformance: member,
  memberReview: (employeeId: string, reviewId: string) =>
    `${member(employeeId)}/reviews/${reviewId}`,
  memberProbationReview: (employeeId: string) =>
    `${member(employeeId)}/probation-review`,
  reviewsOverview: REVIEWS,
  colleagues: `${REVIEWS}/colleagues`,
  submission: (submissionId: string) =>
    `${REVIEWS}/submissions/${submissionId}`,
  sharedReview: (reviewId: string) => `${REVIEWS}/${reviewId}`,
  acknowledge: (reviewId: string) => `${REVIEWS}/${reviewId}/acknowledge`,
  peers: (reviewId: string) => `${REVIEWS}/${reviewId}/peers`,
  oneOnOnes: ONE_ON_ONES,
  oneOnOne: (oneOnOneId: string) => `${ONE_ON_ONES}/${oneOnOneId}`,
  meeting: (oneOnOneId: string, meetingId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/meetings/${meetingId}`,
  completeMeeting: (oneOnOneId: string, meetingId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/meetings/${meetingId}/complete`,
  skipMeeting: (oneOnOneId: string, meetingId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/meetings/${meetingId}/skip`,
  talkingPoints: (oneOnOneId: string, meetingId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/meetings/${meetingId}/talking-points`,
  talkingPoint: (oneOnOneId: string, meetingId: string, pointId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/meetings/${meetingId}/talking-points/${pointId}`,
  actionItems: (oneOnOneId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/action-items`,
  actionItem: (oneOnOneId: string, itemId: string) =>
    `${ONE_ON_ONES}/${oneOnOneId}/action-items/${itemId}`,
};

// ─── Module switch ──────────────────────────────────────────────────────────

/** Whether Performance is on for the company (PF-21); never a 403. */
export async function getPerformanceSummary() {
  const { data } = await api.get<PerformanceSummaryResponse>(endpoints.summary);
  return data;
}

// ─── Reviews ────────────────────────────────────────────────────────────────

export async function getEmployeeReviewsOverview() {
  const { data } = await api.get<EmployeeReviewsOverviewResponse>(
    endpoints.reviewsOverview
  );
  return data;
}

export async function searchPerformanceColleagues(search: string) {
  const { data } = await api.get<PerformancePeopleResponse>(
    endpoints.colleagues,
    { params: { search: search || undefined } }
  );
  return data;
}

export async function getEmployeeReviewSubmission(submissionId: string) {
  const { data } = await api.get<EmployeeReviewSubmissionResponse>(
    endpoints.submission(submissionId)
  );
  return data;
}

export async function saveEmployeeReviewSubmission(
  submissionId: string,
  payload: SaveReviewAnswersPayload
) {
  const { data } = await api.put<EmployeeReviewSubmissionResponse>(
    endpoints.submission(submissionId),
    payload
  );
  return data;
}

export async function getEmployeeSharedReview(reviewId: string) {
  const { data } = await api.get<EmployeeSharedReviewResponse>(
    endpoints.sharedReview(reviewId)
  );
  return data;
}

export async function acknowledgeEmployeeReview(
  reviewId: string,
  payload: AcknowledgeReviewPayload = {}
) {
  const { data } = await api.post<EmployeeSharedReviewResponse>(
    endpoints.acknowledge(reviewId),
    payload
  );
  return data;
}

export async function nominateReviewPeers(
  reviewId: string,
  payload: SetReviewPeersPayload
) {
  const { data } = await api.put<EmployeeReviewsOverviewResponse>(
    endpoints.peers(reviewId),
    payload
  );
  return data;
}

// ─── Manager reviews (the caller writes them, PF-2) ────────────────────────

export async function getManagerReviews() {
  const { data } = await api.get<GetManagerReviewsResponse>(
    endpoints.managerReviews
  );
  return data;
}

export async function getManagerReview(reviewId: string) {
  const { data } = await api.get<ManagerReviewDetailResponse>(
    endpoints.managerReview(reviewId)
  );
  return data;
}

/** A submit before the self review answers 409 `selfPending` (PF-4). */
export async function saveManagerReview(
  reviewId: string,
  payload: SaveReviewAnswersPayload
) {
  const { data } = await api.put<ManagerReviewDetailResponse>(
    endpoints.managerReviewAnswers(reviewId),
    payload
  );
  return data;
}

export async function shareManagerReview(reviewId: string) {
  const { data } = await api.post<ManagerReviewDetailResponse>(
    endpoints.managerReviewShare(reviewId)
  );
  return data;
}

// ─── My team member (managers above them) ──────────────────────────────────

export async function getMemberPerformance(employeeId: string) {
  const { data } = await api.get<MemberPerformanceResponse>(
    endpoints.memberPerformance(employeeId),
    { quietStatuses: [403, 404] }
  );
  return data;
}

export async function getMemberSharedReview(
  employeeId: string,
  reviewId: string
) {
  const { data } = await api.get<MemberSharedReviewResponse>(
    endpoints.memberReview(employeeId, reviewId),
    { quietStatuses: [403, 404] }
  );
  return data;
}

// ─── 1:1s ───────────────────────────────────────────────────────────────────

export async function getOneOnOnes() {
  const { data } = await api.get<GetOneOnOnesResponse>(endpoints.oneOnOnes);
  return data;
}

/**
 * A manager starts 1:1s with someone in their reporting subtree. A 403
 * (`outsideTeam`) is shown by the dialog, never as a redirect home.
 */
export async function createOneOnOne(payload: CreateOneOnOnePayload) {
  const { data } = await api.post<OneOnOneDetailResponse>(
    endpoints.oneOnOnes,
    payload,
    { quietStatuses: [403] }
  );
  return data;
}

/** Manager only: archive the series. */
export async function archiveOneOnOne(oneOnOneId: string) {
  const { data } = await api.delete<PerformanceDeleteResponse>(
    endpoints.oneOnOne(oneOnOneId)
  );
  return data;
}

export async function getOneOnOneDetail(oneOnOneId: string) {
  const { data } = await api.get<OneOnOneDetailResponse>(
    endpoints.oneOnOne(oneOnOneId)
  );
  return data;
}

export async function getOneOnOneMeeting(
  oneOnOneId: string,
  meetingId: string
) {
  const { data } = await api.get<OneOnOneMeetingResponse>(
    endpoints.meeting(oneOnOneId, meetingId)
  );
  return data;
}

export async function updateOneOnOneMeeting(
  oneOnOneId: string,
  meetingId: string,
  payload: UpdateOneOnOneMeetingPayload
) {
  const { data } = await api.put<OneOnOneMeetingResponse>(
    endpoints.meeting(oneOnOneId, meetingId),
    payload
  );
  return data;
}

export async function completeOneOnOneMeeting(
  oneOnOneId: string,
  meetingId: string
) {
  const { data } = await api.post<CloseOneOnOneMeetingResponse>(
    endpoints.completeMeeting(oneOnOneId, meetingId)
  );
  return data;
}

export async function skipOneOnOneMeeting(
  oneOnOneId: string,
  meetingId: string
) {
  const { data } = await api.post<CloseOneOnOneMeetingResponse>(
    endpoints.skipMeeting(oneOnOneId, meetingId)
  );
  return data;
}

export async function addOneOnOneTalkingPoint(
  oneOnOneId: string,
  meetingId: string,
  text: string
) {
  const { data } = await api.post<OneOnOneMeetingResponse>(
    endpoints.talkingPoints(oneOnOneId, meetingId),
    { text }
  );
  return data;
}

export async function updateOneOnOneTalkingPoint(
  oneOnOneId: string,
  meetingId: string,
  pointId: string,
  payload: UpdateTalkingPointPayload
) {
  const { data } = await api.put<OneOnOneMeetingResponse>(
    endpoints.talkingPoint(oneOnOneId, meetingId, pointId),
    payload
  );
  return data;
}

export async function deleteOneOnOneTalkingPoint(
  oneOnOneId: string,
  meetingId: string,
  pointId: string
) {
  const { data } = await api.delete<OneOnOneMeetingResponse>(
    endpoints.talkingPoint(oneOnOneId, meetingId, pointId)
  );
  return data;
}

export async function addOneOnOneActionItem(
  oneOnOneId: string,
  payload: CreateActionItemPayload
) {
  const { data } = await api.post<OneOnOneActionItemResponse>(
    endpoints.actionItems(oneOnOneId),
    payload
  );
  return data;
}

export async function updateOneOnOneActionItem(
  oneOnOneId: string,
  itemId: string,
  payload: UpdateActionItemPayload
) {
  const { data } = await api.put<OneOnOneActionItemResponse>(
    endpoints.actionItem(oneOnOneId, itemId),
    payload
  );
  return data;
}

export async function deleteOneOnOneActionItem(
  oneOnOneId: string,
  itemId: string
) {
  const { data } = await api.delete<PerformanceDeleteResponse>(
    endpoints.actionItem(oneOnOneId, itemId)
  );
  return data;
}

/** "Start probation review" for a team member (spec 5.5, A30). */
export async function startMemberProbationReview(
  employeeId: string,
  payload: StartProbationReviewPayload
) {
  const { data } = await api.post<StartProbationReviewResponse>(
    endpoints.memberProbationReview(employeeId),
    payload
  );
  return data;
}
